import type React from "react";
import { useTheme, Box, Button, Typography, CircularProgress } from "@mui/material";
import { CheckRounded } from "@mui/icons-material";
import { tokens } from "../../../../theme";

interface Props {
  breadcrumb: string;
  tabs: string[];
  activeTab: number;
  onTabChange: (index: number) => void;
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
  saveLabel: string;
  children: React.ReactNode;
}

const PackageBuilderLayout: React.FC<Props> = ({
  breadcrumb,
  tabs,
  activeTab,
  onTabChange,
  onCancel,
  onSave,
  saving,
  saveLabel,
  children,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <Box sx={{ maxWidth: 1180, mx: "auto", px: 3, pb: 12 }}>
      {/* Header row */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          pt: 3,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ fontWeight: 800, color: colors.primary[500] }}
          >
            {breadcrumb === "New Package" ? "Create New Package" : "Edit Package"}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={saving}
            sx={{ textTransform: "none", borderRadius: "10px" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onSave}
            disabled={saving}
            startIcon={
              saving ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <CheckRounded />
              )
            }
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              backgroundColor: "#6b4226",
              "&:hover": { backgroundColor: "#5a3720" },
            }}
          >
            {saveLabel}
          </Button>
        </Box>
      </Box>

      {/* Tab bar */}
      <Box sx={{ display: "flex", gap: 3, mt: 3 }}>
        {tabs.map((tab, i) => (
          <Box
            key={tab}
            onClick={() => onTabChange(i)}
            sx={{
              pb: 1.5,
              cursor: "pointer",
              fontSize: 15,
              fontWeight: activeTab === i ? 700 : 500,
              color: activeTab === i ? "#6b4226" : colors.primary[300],
              borderBottom:
                activeTab === i ? "2px solid #6b4226" : "2px solid transparent",
            }}
          >
            {tab}
          </Box>
        ))}
      </Box>

      {/* Tab content */}
      <Box sx={{ mt: 3 }}>{children}</Box>
    </Box>
  );
};

export default PackageBuilderLayout;
